"use client"

import type { EnemyTypeConfig } from "@/types/enemy.types"
import { ENEMY_SPRITESHEET, ENEMY_SPRITE_COUNT, ENEMY_SPRITE_INDEX } from "@/components/game-ui/enemy-sprite-index"
import { spriteStripStyle } from "@/components/game-ui/sprite-strip"



const getSpeedLabel = (speed: number): string => {
  if (speed <= 1.0) return "Very Slow"
  if (speed <= 1.5) return "Slow"
  if (speed <= 2.0) return "Average"
  if (speed <= 2.5) return "Fast"
  return "Very Fast"
}

interface EnemyDetailsProps {
  enemy: EnemyTypeConfig
}

export const EnemyDetails = ({ enemy }: EnemyDetailsProps) => {
  const spriteIndex = ENEMY_SPRITE_INDEX[enemy.id]
  const hasSprite = spriteIndex !== undefined

  return (
    <div id="enemy-details" className="space-y-4">
      {/* Large enemy portrait */}
      <div className="flex justify-center">
        <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-slate-800 ring-2 ring-amber-600/50">
          {hasSprite ? (
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url(${ENEMY_SPRITESHEET})`,
                ...spriteStripStyle(spriteIndex, ENEMY_SPRITE_COUNT),
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-amber-400">
              ?
            </div>
          )}
        </div>
      </div>

      {/* Enemy name */}
      <h2 className="text-2xl font-bold text-amber-300 text-center">
        {enemy.name}
      </h2>

      {/* Description */}
      <p className="text-gray-300 text-sm text-center italic">
        {enemy.description}
      </p>

      {/* Stats section */}
      <div className="space-y-3 pt-4 border-t border-amber-600/30">
        <h3 className="text-lg font-bold text-amber-400">Base Stats</h3>

        <div className="bg-black/30 rounded-lg p-4 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Health:</span>
              <span className="text-white font-bold">{enemy.stats.health}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">Speed:</span>
              <span className="text-white font-bold">
                {getSpeedLabel(enemy.stats.speed)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">Damage:</span>
              <span className="text-white font-bold">
                {enemy.stats.damage.min}-{enemy.stats.damage.max}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">Armor:</span>
              <span className="text-white font-bold">{enemy.stats.armor}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">Reward:</span>
              <span className="text-green-400 font-bold">${enemy.stats.reward}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">XP:</span>
              <span className="text-blue-400 font-bold">
                {enemy.stats.experienceReward}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Elite ability section */}
      {enemy.eliteAbility && (
        <div className="space-y-2 pt-2 border-t border-purple-600/30">
          <h3 className="text-lg font-bold text-purple-400">Elite Ability</h3>
          <div className="bg-purple-950/30 rounded-lg p-3 space-y-1 border border-purple-600/20">
            <h4 className="text-purple-300 font-bold">
              {enemy.eliteAbility.name}
            </h4>
            <p className="text-gray-300 text-sm">{enemy.eliteAbility.description}</p>
          </div>
        </div>
      )}
    </div>
  )
}
